import React from "react";
import { getBeatPattern, DEFAULT_BEAT } from "../lib/beatSignature";

/**
 * Visual Beat — a silent visual metronome for live performance.
 *
 * Renders a single fixed-position ✓ in the upper-left corner. The ✓ itself is
 * the activation control: press-and-hold for 2 seconds to enable/disable.
 *
 * When enabled:
 *   - Beat 1 of each measure → red ✓ (with a soft glow pulse)
 *   - Other beats           → green ✓ (with a soft glow pulse)
 *   - Driven by the current song's BPM and beat signature (independent of audio)
 *   - Animation: ~140ms scale + glow on each beat
 *
 * Performance:
 *   - Uses one self-rescheduling setTimeout (no rAF loop) → near-zero CPU between beats
 *   - Drift-corrected scheduler keeps timing accurate over long performances
 *   - Hold progress ring only renders while the finger is down
 */
export const VisualBeat = ({ bpm, beat }) => {
  const [enabled, setEnabled] = React.useState(false);
  const [holdProgress, setHoldProgress] = React.useState(0); // 0..1
  const [pulsing, setPulsing] = React.useState(false);
  const [beatIdx, setBeatIdx] = React.useState(0);

  const enabledRef = React.useRef(false);
  React.useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const tickTimerRef = React.useRef(null);
  const pulseTimerRef = React.useRef(null);
  const holdTimerRef = React.useRef(null);
  const holdRafRef = React.useRef(null);
  const holdStartRef = React.useRef(0);
  const beatStartRef = React.useRef(0);
  // Absolute tick counter — never wraps. The visual beat index uses modulo.
  // Wrapping the scheduler counter caused `nextAt` to land in the past every
  // measure, which collapsed setTimeout into a tight 0-delay loop and made the
  // metronome appear to "stop" after the first measure.
  const tickIdxRef = React.useRef(0);

  const sig = beat || DEFAULT_BEAT;
  const pattern = getBeatPattern(sig);
  const beatsPerMeasure = pattern.beats;
  const validBpm = Number.isFinite(bpm) && bpm > 20 ? bpm : 100;

  // Read BPM/beat-sig through refs inside the scheduler so live tempo changes
  // never tear down the scheduler chain.
  const validBpmRef = React.useRef(validBpm);
  const beatsPerMeasureRef = React.useRef(beatsPerMeasure);
  React.useEffect(() => { validBpmRef.current = validBpm; }, [validBpm]);
  React.useEffect(() => { beatsPerMeasureRef.current = beatsPerMeasure; }, [beatsPerMeasure]);

  const clearTick = React.useCallback(() => {
    if (tickTimerRef.current) {
      clearTimeout(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    if (pulseTimerRef.current) {
      clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = null;
    }
  }, []);

  const firePulse = React.useCallback(() => {
    setPulsing(true);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setPulsing(false), 140);
  }, []);

  const scheduleNext = React.useCallback(() => {
    const interval = 60000 / validBpmRef.current;
    const nextAt = beatStartRef.current + (tickIdxRef.current + 1) * interval;
    const delay = Math.max(0, nextAt - performance.now());
    tickTimerRef.current = setTimeout(() => {
      if (!enabledRef.current) return;
      tickIdxRef.current += 1;
      const inMeasure = tickIdxRef.current % beatsPerMeasureRef.current;
      setBeatIdx(inMeasure);
      firePulse();
      // Always reschedule. The chain only ever stops when enabledRef goes false
      // (i.e. user did a 2-second long-press to toggle OFF).
      scheduleNext();
    }, delay);
  }, [firePulse]);

  React.useEffect(() => {
    if (enabled) {
      clearTick();
      beatStartRef.current = performance.now();
      tickIdxRef.current = 0;
      setBeatIdx(0);
      firePulse();
      scheduleNext();
    } else {
      clearTick();
      setPulsing(false);
    }
    return clearTick;
  }, [enabled, scheduleNext, clearTick, firePulse]);

  const cancelHold = React.useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdRafRef.current) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    setHoldProgress(0);
  }, []);

  const onHoldStart = (e) => {
    e.preventDefault();
    if (holdTimerRef.current) return;
    holdStartRef.current = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - holdStartRef.current) / 2000);
      setHoldProgress(p);
      if (p < 1) holdRafRef.current = requestAnimationFrame(tick);
    };
    holdRafRef.current = requestAnimationFrame(tick);
    holdTimerRef.current = setTimeout(() => {
      setEnabled((v) => !v);
      if (navigator.vibrate) {
        try { navigator.vibrate(30); } catch (_) { /* noop */ }
      }
      cancelHold();
    }, 2000);
  };

  const isAccent = pattern.accents.includes(beatIdx);
  const activeColor = isAccent ? "#ef4444" : "#22c55e"; // red-500 / green-500
  const idleColor = "rgba(255,255,255,0.28)";
  const color = enabled ? activeColor : idleColor;
  const glowColor = isAccent ? "rgba(239,68,68,0.85)" : "rgba(34,197,94,0.85)";
  // Big-mode glow: wider halo so it reads from peripheral vision on stage.
  const glow = enabled && pulsing
    ? `0 0 48px ${glowColor}, 0 0 18px ${glowColor}, 0 0 6px ${glowColor}`
    : "none";
  const scale = enabled && pulsing ? 1.16 : 1;
  // Stroke weight via -webkit-text-stroke for thicker pulse without layout shift
  const strokeWidth = enabled && pulsing ? "3.5px" : "1.2px";

  return (
    <div
      onPointerDown={onHoldStart}
      onPointerUp={cancelHold}
      onPointerCancel={cancelHold}
      onPointerLeave={cancelHold}
      onContextMenu={(e) => e.preventDefault()}
      className="fixed top-3 right-3 z-[60] select-none"
      style={{
        width: "clamp(110px, 16vw, 170px)",
        height: "clamp(110px, 16vw, 170px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
        cursor: "pointer",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
      data-testid="visual-beat-toggle"
      title="Maintenir 2s pour activer/désactiver le Visual Beat"
      aria-label={enabled ? "Visual Beat actif — maintenir 2s pour désactiver" : "Visual Beat — maintenir 2s pour activer"}
      role="switch"
      aria-checked={enabled}
    >
      {holdProgress > 0 && (
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="2" />
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke={enabled ? "rgba(255,255,255,0.7)" : "var(--gl-gold, #ffd166)"}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 46}
            strokeDashoffset={2 * Math.PI * 46 * (1 - holdProgress)}
            transform="rotate(-90 50 50)"
            style={{ transition: "stroke-dashoffset 40ms linear" }}
          />
        </svg>
      )}
      <span
        style={{
          color,
          fontSize: "clamp(88px, 13vw, 140px)",
          lineHeight: 1,
          fontWeight: 900,
          WebkitTextStroke: `${strokeWidth} ${color}`,
          textShadow: glow,
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          transition:
            "transform 130ms cubic-bezier(.2,.8,.2,1), text-shadow 130ms ease-out, color 90ms linear, -webkit-text-stroke 130ms ease-out",
          display: "inline-block",
          fontFamily: 'system-ui, -apple-system, "SF Pro Display", sans-serif',
          willChange: "transform, text-shadow",
        }}
        aria-hidden="true"
      >
        ✓
      </span>
    </div>
  );
};

export default VisualBeat;
